import logger from './logger';

/**
 * Authentication Test Utility
 * Tests different authentication methods with the backend API
 */

const API_BASE_URL = 'https://api.visibeen.com/api/v1/customer/profile';
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

/**
 * Test different authentication methods with the backend
 */
export async function testAuthentication() {
  logger.log('🔐 Starting authentication tests...');
  logger.log('🔐 API Key available:', API_KEY ? 'Yes' : 'No');
  logger.log('🔐 API Key (first 10 chars):', API_KEY ? API_KEY.substring(0, 10) + '...' : 'None');
  
  const testEndpoint = `${API_BASE_URL}/textsearch`;
  const testParams = { query: 'test' };
  
  const authMethods = [
    {
      name: 'No Authentication',
      setup: (url, headers) => {
        // No auth headers or params
      }
    },
    {
      name: 'Query Parameter (key)',
      setup: (url, headers) => {
        if (API_KEY) url.searchParams.set('key', API_KEY);
      }
    },
    {
      name: 'Query Parameter (apikey)',
      setup: (url, headers) => {
        if (API_KEY) url.searchParams.set('apikey', API_KEY);
      }
    },
    {
      name: 'Query Parameter (api_key)',
      setup: (url, headers) => {
        if (API_KEY) url.searchParams.set('api_key', API_KEY);
      }
    },
    {
      name: 'Header: X-API-Key',
      setup: (url, headers) => {
        if (API_KEY) headers['X-API-Key'] = API_KEY;
      }
    },
    {
      name: 'Header: Authorization Bearer',
      setup: (url, headers) => {
        if (API_KEY) headers['Authorization'] = `Bearer ${API_KEY}`;
      }
    },
    {
      name: 'Header: X-Google-API-Key',
      setup: (url, headers) => {
        if (API_KEY) headers['X-Google-API-Key'] = API_KEY;
      }
    },
    {
      name: 'Header: Google-API-Key',
      setup: (url, headers) => {
        if (API_KEY) headers['Google-API-Key'] = API_KEY;
      }
    },
    {
      name: 'Combined: Query + Headers',
      setup: (url, headers) => {
        if (API_KEY) {
          url.searchParams.set('key', API_KEY);
          headers['X-API-Key'] = API_KEY;
          headers['Authorization'] = `Bearer ${API_KEY}`;
        }
      }
    }
  ];
  
  const results = [];
  
  for (const method of authMethods) {
    logger.log(`\n🔐 Testing: ${method.name}`);
    
    try {
      const url = new URL(testEndpoint);
      Object.entries(testParams).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Apply authentication method
      method.setup(url, headers);
      
      logger.log(`🔐 URL: ${url.toString()}`);
      logger.log(`🔐 Headers:`, Object.keys(headers).filter(k => k !== 'Content-Type'));
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: headers
      });
      
      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch {
        responseData = responseText;
      }
      
      const result = {
        method: method.name,
        status: response.status,
        statusText: response.statusText,
        success: response.ok,
        response: responseData,
        headers: Object.fromEntries(response.headers.entries())
      };
      
      results.push(result);
      
      if (response.ok) {
        logger.log(`✅ ${method.name}: SUCCESS (${response.status})`);
        logger.log(`✅ Response:`, responseData);
      } else {
        logger.log(`❌ ${method.name}: FAILED (${response.status})`);
        logger.log(`❌ Error:`, responseData);
      }
      
    } catch (error) {
      logger.log(`❌ ${method.name}: ERROR - ${error.message}`);
      results.push({
        method: method.name,
        status: 'ERROR',
        success: false,
        error: error.message
      });
    }
  }
  
  logger.log('\n🔐 Authentication Test Summary:');
  logger.log('================================');
  
  const successful = results.filter(r => r.success);
  const failed = results.filter(r => !r.success);
  
  if (successful.length > 0) {
    logger.log(`✅ Successful methods (${successful.length}):`);
    successful.forEach(r => {
      logger.log(`  - ${r.method}: ${r.status}`);
    });
  }
  
  if (failed.length > 0) {
    logger.log(`❌ Failed methods (${failed.length}):`);
    failed.forEach(r => {
      logger.log(`  - ${r.method}: ${r.status || r.error}`);
    });
  }
  
  // Provide recommendations
  logger.log('\n🔐 Recommendations:');
  if (successful.length > 0) {
    logger.log('✅ Use one of the successful authentication methods above');
    logger.log('✅ Update LivePlacesAPIService to use the working method');
  } else {
    logger.log('❌ No authentication method worked');
    logger.log('❌ Backend server configuration issues:');
    logger.log('  1. Backend may not be configured to accept API keys');
    logger.log('  2. Backend may expect a different authentication format');
    logger.log('  3. Backend GOOGLE_API_KEY environment variable may be missing');
    logger.log('  4. Backend may require session-based authentication');
    logger.log('  5. CORS configuration may be blocking requests');
  }
  
  return results;
}

/**
 * Test a specific authentication method
 */
export async function testSpecificAuth(methodName, customSetup = null) {
  logger.log(`🔐 Testing specific method: ${methodName}`);
  
  const url = new URL(`${API_BASE_URL}/textsearch`);
  url.searchParams.set('query', 'restaurant');
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (customSetup) {
    customSetup(url, headers);
  } else {
    // Default to query parameter method
    if (API_KEY) url.searchParams.set('key', API_KEY);
  }
  
  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: headers
    });
    
    const responseText = await response.text();
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch {
      responseData = responseText;
    }
    
    logger.log(`🔐 Status: ${response.status}`);
    logger.log(`🔐 Response:`, responseData);
    
    return {
      success: response.ok,
      status: response.status,
      data: responseData
    };
    
  } catch (error) {
    logger.error(`🔐 Error:`, error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Quick test with the most common authentication methods
 */
export async function quickAuthTest() {
  logger.log('🔐 Quick authentication test...');
  
  // Test the most common methods first
  const commonMethods = [
    { name: 'Query Parameter', setup: (url) => url.searchParams.set('key', API_KEY) },
    { name: 'X-API-Key Header', setup: (url, headers) => headers['X-API-Key'] = API_KEY },
    { name: 'Authorization Bearer', setup: (url, headers) => headers['Authorization'] = `Bearer ${API_KEY}` }
  ];
  
  for (const method of commonMethods) {
    const result = await testSpecificAuth(method.name, method.setup);
    if (result.success) {
      logger.log(`✅ Found working method: ${method.name}`);
      return method;
    }
  }
  
  logger.log('❌ No common authentication method worked');
  return null;
}

// Make functions available globally for console testing
if (typeof window !== 'undefined') {
  window.testAuthentication = testAuthentication;
  window.testSpecificAuth = testSpecificAuth;
  window.quickAuthTest = quickAuthTest;
}
